<template>
    <BaseLink
        :href="data.return_url"
        class="group relative flex items-start gap-3 md:gap-4 px-4 py-3 md:px-5 md:py-(--spacing-history-row-y-md) text-left transition-all w-full rounded-lgr hover:bg-surface-hover hover:border hover:border-history-row-hover-border active:bg-history-row-active-bg active:border active:border-history-row-hover-border border border-transparent"
        :data="{ chat_source: CHATSOURCE.PAST_CONVERSATIONS }">
        <!-- Main content -->
        <div class="flex-1 min-w-0">
            <!-- Date above summary -->
            <p class="scaffold-body-small text-history-date mb-1">
                {{ formattedDate }}<span v-if="data.message_count"> &middot; {{ data.message_count }} messages</span>
            </p>

            <!-- Title -->
            <p v-if="!isRenaming" class="nadia-chat-semibold text-history-title fs-mask truncate">
                {{ truncate(data.title, { length: 120, omission: "..." }) }}
            </p>
            <div v-else class="flex w-full gap-2 items-center" @click.prevent.stop @keyup.prevent.stop>
                <input
                    ref="renameInput"
                    v-model.trim="newTitle"
                    class="grow body-small focus-visible:outline-hidden rounded-lg p-2 w-full border border-valence-grey-300 fs-mask"
                    :maxlength="120"
                    @keydown.enter.prevent.stop="handleSaveTitle"
                    @keydown.escape.stop="isRenaming = false" />
                <button
                    aria-label="Save conversation title"
                    :disabled="!newTitle"
                    class="rounded-full hover:bg-valence-primary-500/10 px-1 flex justify-center items-center disabled:text-valence-grey-750"
                    @click="handleSaveTitle">
                    <i class="bi bi-check text-xl" aria-hidden="true" />
                </button>
                <button aria-label="Cancel rename" class="rounded-full hover:bg-valence-primary-500/10 px-1 flex justify-center items-center" @click="isRenaming = false">
                    <i class="bi bi-x text-xl" aria-hidden="true" />
                </button>
            </div>
        </div>

        <!-- Actions column -->
        <div class="shrink-0 flex items-center gap-1 self-center" @click.stop.prevent>
            <BaseDropdownMenu :sections="menuSections" side="left" align="center" :side-offset="10" @select="handleMenuSelect" @close-auto-focus="handleMenuCloseAutoFocus">
                <template #trigger>
                    <button
                        type="button"
                        aria-label="Conversation options"
                        class="hist-kebab w-8 h-8 rounded-full inline-flex items-center justify-center text-valence-grey-600 hover:bg-history-menu-hover-bg aria-expanded:bg-history-menu-hover-bg transition-colors"
                        @click.stop.prevent>
                        <i class="bi bi-three-dots text-sm" aria-hidden="true" />
                    </button>
                </template>
            </BaseDropdownMenu>
        </div>
    </BaseLink>
</template>

<script setup lang="ts">
import { convertToDateTime } from "/js/dateUtils";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { CHATSOURCE } from "~vue/utils/constants";
import truncate from "lodash/truncate";
import { computed, nextTick, ref } from "vue";

export interface ConversationData {
    kind: "v1" | "v2";
    hashid: string;
    uuid: string;
    title: string;
    return_url: string;
    updated_at: string;
    message_count: number;
    preview: string | null;
}

interface Props {
    deletionDisabled?: boolean;
    data: ConversationData;
}

const { data, deletionDisabled = false } = defineProps<Props>();

const emit = defineEmits<{
    delete: [payload: { kind: "v1" | "v2"; hashid: string; uuid: string }];
    rename: [payload: { kind: "v1" | "v2"; hashid: string; uuid: string; newTitle: string }];
}>();

const formattedDate = computed(() => {
    const dt = convertToDateTime(data.updated_at);
    const now = convertToDateTime(new Date().toISOString());
    const diffDays = now.startOf("day").diff(dt.startOf("day"), "days").days;

    if (diffDays > 300) {
        return dt.toFormat("LLL d, yyyy");
    }
    return dt.toFormat("LLL d");
});

const menuSections = computed<DropdownMenuSection[]>(() => {
    const items: DropdownMenuItem[] = [{ id: "rename", type: "button", label: "Rename", icon: "bi bi-pencil" }];
    // Delete is hidden when the user is on legal/retention hold; rename stays
    // available because the v1 server endpoint only blocks delete.
    if (!deletionDisabled) {
        items.push({ id: "delete", type: "button", label: "Delete", icon: "bi bi-trash" });
    }
    return [{ id: "actions", items }];
});

const handleDeletePastConversation = () => {
    emit("delete", { kind: data.kind, hashid: data.hashid, uuid: data.uuid });
};

const renameInput = ref<HTMLInputElement>();
const isRenaming = ref(false);
const newTitle = ref("");

const handleStartRename = () => {
    newTitle.value = data.title ?? "";
    isRenaming.value = true;
};

const handleMenuCloseAutoFocus = async (event: Event) => {
    if (!isRenaming.value) {
        return;
    }

    event.preventDefault();
    await nextTick();
    // Reka removes its modal focus scope in a timer after this event.
    globalThis.setTimeout(() => {
        renameInput.value?.focus();
        renameInput.value?.select();
    }, 0);
};

const handleMenuSelect = (item: DropdownMenuItem) => {
    if (item.id === "rename") {
        handleStartRename();
    } else if (item.id === "delete") {
        handleDeletePastConversation();
    }
};

const handleSaveTitle = () => {
    if (!newTitle.value) {
        return;
    }
    isRenaming.value = false;
    emit("rename", { kind: data.kind, hashid: data.hashid, uuid: data.uuid, newTitle: newTitle.value });
};
</script>

<style scoped>
@reference "../../../css/tailwind.css";

/* The spec's kebab is hidden until the row is hovered, but only for pointers
   that can hover: touch users keep it always visible, since focus-visible
   only rescues keyboards. Flag off it stays always visible as today. */
@media (hover: hover) {
    html[data-theme="tonal"] .hist-kebab {
        opacity: 0;
        transition: opacity 0.15s ease;
    }

    html[data-theme="tonal"] .group:hover .hist-kebab,
    html[data-theme="tonal"] .hist-kebab:focus-visible,
    html[data-theme="tonal"] .hist-kebab[aria-expanded="true"] {
        opacity: 1;
    }
}
</style>
